import React, { useState } from 'react';

import {
  CertificationRatingStatus,
  ReviewStatus
} from 'types/graphql-api.generated';

import Button from 'common/components/Button';
import UserRating from 'common/components/UserRating';
import Avatar from 'common/components/user/Avatar';
import { ROUTE } from 'common/configuration/constants';
import lazyModal from 'common/module/ui/lazy-modal';
import { ModalNames } from 'common/module/ui/lazy-modal/constant-types';
import { cutSpoilers } from 'common/spoilers/utils';
import trans from 'common/tools/translations/trans';
import { GA4Entity } from 'common/tracking/helpers';
import { hit as hit_jan } from 'common/tracking/jan';

import Card from 'website/components/UserOpinion/ReviewCardBam/Card';
import ReviewText from 'website/components/UserOpinion/ReviewCardBam/ReviewText';
import ReviewLink from 'website/components/opinion/ReviewLink';

import {
  ReviewStatusDenied,
  ReviewStatusBlocked
} from './ReviewStatusMessages';

export type ReviewCardBamProps = {
  id: string;
  internalId: number;
  title: string;
  secondTitle?: string;
  image?: string;
  rating: number;
  review: string;
  date: Date;
  status: ReviewStatus;
  typename: string;
  cutThreshold?: number;
  series?: object;
  entityHref: string;
  onEditClick?: (id: string) => void;
  entityId: string;
  certificationRating?: CertificationRatingStatus | null;
  hasShowtime?: boolean;
};

const getStatusLabel = (title: string, status: ReviewStatus) => {
  switch (status) {
    case ReviewStatus.Blocked:
      return trans('review.validation-status.blocked', { title });
    case ReviewStatus.Denied:
      return trans('review.validation-status.denied');
    case ReviewStatus.Pending:
      return trans('review.validation-status.pending');
    case ReviewStatus.Reported:
      return trans('review.validation-status.reported');
    default:
      return null;
  }
};

const ReviewCardBam: React.FC<ReviewCardBamProps> = ({
  title,
  image,
  rating,
  review,
  status,
  cutThreshold = 800,
  entityId,
  internalId,
  typename,
  certificationRating,
  hasShowtime = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const isVerified = certificationRating === CertificationRatingStatus.Accepted;

  const needsCut = review.length > cutThreshold;
  const renderCollapsed = needsCut && !expanded;
  const _review = renderCollapsed ? cutSpoilers(review, cutThreshold) : review;

  const statusLabel = getStatusLabel(title, status);
  const isValidated = !statusLabel;

  const showStandardBlock = [
    ReviewStatus.Accepted,
    ReviewStatus.Published,
    ReviewStatus.MustBeChecked,
    ReviewStatus.BeingChecked
  ].includes(status);

  const renderStatusContent = () => {
    switch (status) {
      case ReviewStatus.Denied:
        return <ReviewStatusDenied entityId={entityId} />;
      case ReviewStatus.Blocked:
        return <ReviewStatusBlocked entityId={entityId} />;
      default:
        return null;
    }
  };

  return (
    <div className="review-card-bam cf">
      <Card className="review-card-entity-image">
        <Avatar src={image} alt="avatar" size={60} />
      </Card>

      <div className="review-card-bam-content">
        <div className="review-card-bam-holder">
          <div className="review-card-bam-rating">
            <span className="review-card-bam-rating-text">
              {trans('bam.my-review')}
            </span>
            <UserRating size="medium" value={rating} readOnly />
            <span className="review-card-bam-rating-number">{rating}</span>
            {isVerified && (
              <span className="verified-rating-indicator">
                <span className="badge-verified" />
                <span className="verified-rating-text">
                  {trans('bam.verified.status')}
                </span>
              </span>
            )}
            <div className="review-card-bam-review-link">
              <ReviewLink
                entityId={entityId}
                showLabel={false}
                className="review-edit-bam"
              />
            </div>
            {hasShowtime &&
              certificationRating === null &&
              typename === 'Movie' && (
                <Button
                  size="xs"
                  className="button-verified-rating"
                  onClick={() => {
                    hit_jan('internal_click', {
                      position_name: 'button_verify',
                      movie_entity: GA4Entity(internalId ?? undefined, title),
                      site_route: ROUTE
                    });
                    lazyModal.open(ModalNames.VERIFY_RATING_MODAL, {
                      fromUserRating: true,
                      entityId: entityId,
                      entity: {
                        internalId: internalId ?? undefined,
                        title: title ?? undefined,
                        typename: typename
                      }
                    });
                  }}
                >
                  {trans('review.verified.label')}
                </Button>
              )}
          </div>
        </div>

        <div>
          {showStandardBlock ? (
            <div className="review-card-content">
              <div className="review-card-review-text">
                <ReviewText text={_review} />
                {renderCollapsed && '...'}
              </div>

              {renderCollapsed && (
                <div
                  className="review-card-read-more"
                  onClick={() => setExpanded(true)}
                >
                  {trans('review.read-more')}
                </div>
              )}

              {!isValidated && (
                <div className="review-card-status">{statusLabel}</div>
              )}
            </div>
          ) : (
            renderStatusContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewCardBam;
